import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Confirmation from './Confirmation';
import { DEFAULT_COMMUNITY_SOLR_VERSION, SOLR_VERSIONS } from './constants';
import InstanceForm from './InstanceForm';
import Payment from './Payment';
import Plan from './Plan';
import { schema } from './schema';
import { INSTANCE_CATEGORY, INSTANCE_PLANS } from '../../../constants';
import { projectSetupComplete } from '../../../utils/paymentUtils';
import ActionButton from '../../elements/action-button/ActionButton';

const InstanceStepper = ({
  community,
  payment,
  project,
  onCheckDuplicate,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [step, setStep] = useState(0);

  const handleBack = () => setStep(step - 1);
  const handleNext = () => setStep(step + 1);

  const stepTitles = [
    t('planSelection', { ns: 'instance' }),
    t('methodInfo', { ns: 'sectionTitles' }),
    t('instanceInfo', { ns: 'instance' }),
    t('finalConfirmation', { ns: 'instance' }),
  ];

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema(onCheckDuplicate)),
    defaultValues: {
      plan: INSTANCE_PLANS.MEDIUM,
      card: payment.card?.[0],
      solrVersion: '',
      name: '',
      description: '',
      subdomain: '',
      cidrs: [],
    },
  });

  const { cards, billingInfo, managers } = payment;
  const { plan, name, description, subdomain } = methods.watch();
  const isCommunity = plan === INSTANCE_CATEGORY.COMMUNITY;

  useEffect(() => {
    isCommunity
      ? methods.setValue('solrVersion', DEFAULT_COMMUNITY_SOLR_VERSION.value)
      : methods.setValue('solrVersion', SOLR_VERSIONS[0].value);
  }, [plan]);

  const canProceed = () => {
    const errors = methods.formState.errors;
    const noErrors = Object.keys(errors).length === 0;

    switch (step) {
      case 0:
        return !errors?.plan;
      case 1:
        return (
          isCommunity || projectSetupComplete(cards, billingInfo, managers)
        );
      case 2:
        return !!name && !!subdomain && noErrors;
      default:
        return false;
    }
  };

  useEffect(() => {
    canProceed();
  }, [plan, name, description, subdomain, step]);

  return (
    <FormProvider {...methods}>
      <Stepper activeStep={step} alternativeLabel sx={{ mt: 2, mb: 2 }}>
        {stepTitles.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <form>
        <Container maxWidth="md">
          {step === 0 && <Plan community={community} />}
          {step === 1 && <Payment projectId={project.id} payment={payment} />}
          {step === 2 && <InstanceForm projectName={project.name} />}
          {step === 3 && (
            <Confirmation card={cards} billingInfo={billingInfo} />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Button
              aria-label="previous step"
              variant="outlined"
              disabled={!step}
              onClick={handleBack}
            >
              {t('previousStep', { ns: 'buttons' })}
            </Button>
            {step === 3 && (
              <ActionButton
                aria-label="confirm"
                clickAction={methods.handleSubmit(onSubmit)}
              >
                {t('confirm', { ns: 'buttons' })}
              </ActionButton>
            )}
            {step !== 3 && (
              <Button
                aria-label="next step"
                onClick={handleNext}
                disabled={!canProceed()}
              >
                {t('nextStep', { ns: 'buttons' })}
              </Button>
            )}
          </Box>
        </Container>
      </form>
    </FormProvider>
  );
};

InstanceStepper.propTypes = {
  community: PropTypes.number.isRequired,
  payment: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  onCheckDuplicate: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default InstanceStepper;
